import React, { useState, useEffect } from 'react';
import { startInterview, registerUser, activatePlan, getUserStatus, createPaymentOrder } from '../services/api';
import './SetupScreen.css';

const ROLES = [
  'Java Developer', 'Frontend Developer', 'Backend Developer',
  'Full Stack Developer', 'React Developer', 'Node.js Developer',
  'Python Developer', 'DevOps Engineer', 'Data Engineer',
  'Machine Learning Engineer', 'Android Developer', 'iOS Developer',
  'Software Engineer', 'System Design Engineer',
];

const STACKS = [
  'Java, Spring Boot', 'React, JavaScript', 'Node.js, Express',
  'React, Node.js, MongoDB', 'Python, Django', 'Python, FastAPI',
  'Vue.js, JavaScript', 'Angular, TypeScript', 'Go, PostgreSQL',
  'Ruby on Rails', 'PHP, Laravel', 'Kotlin, Android',
  'Swift, iOS', 'AWS, Docker, Kubernetes', 'Python, TensorFlow, PyTorch',
];

const EXPERIENCE_LEVELS = [
  { value: 'fresher', label: 'Fresher',         desc: '0–1 years' },
  { value: 'junior',  label: 'Junior',           desc: '1–3 years' },
  { value: 'mid',     label: 'Mid-Level',        desc: '3–5 years' },
  { value: 'senior',  label: 'Senior',           desc: '5–8 years' },
  { value: 'lead',    label: 'Lead / Architect', desc: '8+ years'  },
];

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    badge: null,
    price: null,
    priceNote: null,
    interviews: '1 mock interview session',
    duration: '30 Days',
    features: [
      { text: '1 mock interview session',   active: true  },
      { text: '3 questions per session',    active: true  },
      { text: 'Basic AI feedback',          active: true  },
      { text: 'Text answer fallback',       active: true  },
      { text: 'Valid for 30 days',          active: true  },
      { text: 'Follow-up questions',        active: false },
      { text: 'Detailed evaluation report', active: false },
      { text: 'Priority support',           active: false },
    ],
    btnText: 'Start for Free',
    btnStyle: 'free',
    color: '#6b7280',
  },
  {
    id: 'starter',
    name: 'Starter',
    badge: null,
    price: 199,
    priceNote: 'one-time',
    interviews: '2 Full Interviews',
    duration: '30 Days',
    features: [
      { text: '2 full mock interviews',        active: true  },
      { text: 'Unlimited questions/session',   active: true  },
      { text: 'AI follow-up questions',        active: true  },
      { text: 'Voice + Text answers',          active: true  },
      { text: 'Final score & grade',           active: true  },
      { text: 'Priority support',              active: false },
      { text: 'Advanced analytics',            active: false },
    ],
    btnText: 'Get Starter — ₹199',
    btnStyle: 'starter',
    color: '#3b82f6',
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: '⭐ Most Popular',
    price: 499,
    priceNote: 'one-time',
    interviews: '6 Full Interviews',
    duration: '30 Days',
    features: [
      { text: '6 full mock interviews',         active: true },
      { text: 'All experience levels',          active: true },
      { text: 'AI follow-up questions',         active: true },
      { text: 'Voice + Text answers',           active: true },
      { text: 'Detailed evaluation report',     active: true },
      { text: 'Hire / No-hire recommendation',  active: true },
    ],
    btnText: 'Get Pro — ₹499',
    btnStyle: 'pro',
    color: '#7c3aed',
  },
  {
    id: 'premium',
    name: 'Premium',
    badge: '⭐ Best Value',
    price: 799,
    priceNote: 'one-time',
    interviews: '10 Full Interviews',
    duration: '30 Days',
    features: [
      { text: '10 full mock interviews',        active: true },
      { text: 'All experience levels',          active: true },
      { text: 'AI follow-up questions',         active: true },
      { text: 'Voice + Text answers',           active: true },
      { text: 'Detailed evaluation report',     active: true },
      { text: 'Hire / No-hire recommendation',  active: true },
      { text: 'Priority support',               active: true },
      { text: 'Partial session evaluation',     active: true },
    ],
    btnText: 'Get Premium — ₹799',
    btnStyle: 'premium',
    color: '#f59e0b',
  },
];

const AnimatedBg = () => (
  <div className="setup-bg">
    <div className="orb orb-1" /><div className="orb orb-2" />
    <div className="orb orb-3" /><div className="grid-lines" />
  </div>
);

// ─────────────────────────────────────────────────────
// ✅ Component starts here — ALL useState hooks inside
// ─────────────────────────────────────────────────────
export default function SetupScreen({ onStart, savedUser, onSaveUser, onViewHistory, onLogout }) {

  const [form, setForm] = useState({
    candidateName:   savedUser?.name  || '',
    jobRole:         'Java Developer',
    techStack:       'Java, Spring Boot',
    experienceLevel: 'mid',
    durationMinutes: 30,
  });

  const [userId,      setUserId]      = useState(savedUser?.userId  || null);
  const [userStatus,  setUserStatus]  = useState(savedUser          || null);
  const [emailInput,  setEmailInput]  = useState(savedUser?.email   || '');
  const [mobileInput, setMobileInput] = useState(savedUser?.mobile  || ''); // ✅ INSIDE component
  const [registering, setRegistering] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // ── Load Cashfree SDK ──
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // ── Re-fetch user status from DB on mount ──
  useEffect(() => {
    if (savedUser?.userId) {
      getUserStatus(savedUser.userId)
        .then(({ data }) => {
          setUserStatus(data);
          setUserId(data.userId);
          onSaveUser(data);
          if (!data.canStartInterview) setShowPaywall(true);
        })
        .catch(() => {
          onLogout();
        });
    }
  }, []);

  // ── Register handler ──
  const handleRegister = async () => {
    if (!form.candidateName.trim()) {
      setError('Please enter your name'); return;
    }
    if (!emailInput.trim()) {
      setError('Please enter your email'); return;
    }
    if (!mobileInput.trim()) {
      setError('Please enter your mobile number'); return;
    }
    if (!/^[6-9]\d{9}$/.test(mobileInput.trim())) {
      setError('Enter a valid 10-digit Indian mobile number'); return;
    }

    setRegistering(true);
    setError('');
    try {
      const { data } = await registerUser({
        email:  emailInput.trim().toLowerCase(),
        name:   form.candidateName.trim(),
        mobile: mobileInput.trim(),
      });
      setUserId(data.userId);
      setUserStatus(data);
      onSaveUser(data);
      if (!data.canStartInterview) setShowPaywall(true);
    } catch (e) {
      console.error('Register error:', e.response?.status, e.response?.data);
      setError(
        e.response?.data?.error || 'Something went wrong. Please try again.'
      );
    } finally {
      setRegistering(false);
    }
  };

  // ── Subscribe / Payment handler ──
  const handleSubscribe = async (plan) => {
    setError('');
    setLoading(true);
    try {
      const { data: order } = await createPaymentOrder({ userId, plan });
      const cashfree = window.Cashfree({ mode: 'sandbox' });
      cashfree.checkout({
        paymentSessionId: order.orderToken,
        redirectTarget:   '_self',
      });
    } catch (e) {
      console.error('Payment error:', e);
      setError('Could not initiate payment. Please try again.');
      setLoading(false);
    }
  };

  // ── Start interview handler ──
  const handleStart = async () => {
    if (!form.candidateName.trim()) { setError('Please enter your name'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await startInterview({
        candidateName:   form.candidateName,
        jobRole:         form.jobRole,
        techStack:       form.techStack,
        experienceLevel: form.experienceLevel,
        durationMinutes: 30,
        userId,
      });
      onStart(form, data);
    } catch (e) {
      if (e.response?.status === 402) {
        setShowPaywall(true);
      } else {
        setError('Failed to connect. Make sure the backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════
  // SCREEN 1 — Email / Mobile gate
  // ════════════════════════════════════════
  if (!userId) {
    return (
      <div className="setup-root">
        <AnimatedBg />
        <div className="setup-container">

          <div className="setup-header">
            <div className="aria-logo">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="23" stroke="url(#lg1)" strokeWidth="2"/>
                <circle cx="24" cy="24" r="10" fill="url(#lg2)" opacity="0.8"/>
                <circle cx="24" cy="24" r="5" fill="#fff" opacity="0.9"/>
                <defs>
                  <linearGradient id="lg1" x1="0" y1="0" x2="48" y2="48">
                    <stop stopColor="#6394ff"/><stop offset="1" stopColor="#a78bfa"/>
                  </linearGradient>
                  <linearGradient id="lg2" x1="0" y1="0" x2="48" y2="48">
                    <stop stopColor="#6394ff"/><stop offset="1" stopColor="#f472b6"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h1 className="setup-title">Meet <span className="gradient-text">URU</span></h1>
            <p className="setup-subtitle">Your AI Technical Interviewer</p>
            <p className="setup-desc">
              Enter your details to get started. You get{' '}
              <strong>1 free interview</strong> — no credit card needed.
            </p>
          </div>

          <div className="setup-form">
            <div className="form-group">
              <label>Your Name</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={form.candidateName}
                onChange={e => setForm({ ...form, candidateName: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleRegister()}
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRegister()}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label>Mobile Number</label>
              <div className="mobile-input-wrap">
                <span className="mobile-prefix">🇮🇳 +91</span>
                <input
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={mobileInput}
                  maxLength={10}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    setMobileInput(val);
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleRegister()}
                  autoComplete="tel"
                />
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <button
              className="start-btn"
              disabled={registering}
              onClick={handleRegister}
            >
              {registering
                ? <span className="btn-loading"><span className="spinner" /> Checking...</span>
                : <span>Continue →</span>
              }
            </button>
          </div>

          <div className="free-badge">
            🎁 <strong>1 free interview</strong> — no subscription needed to start
          </div>

          <div className="features-row">
            {[
              { icon: '🎤', label: 'Voice Recognition', desc: 'Speak your answers naturally'    },
              { icon: '🔊', label: 'AI Voice',          desc: 'URU speaks to you in real-time'  },
              { icon: '📊', label: 'Smart Evaluation',  desc: 'Detailed feedback & scoring'     },
            ].map(f => (
              <div className="feature-card" key={f.label}>
                <span className="feature-icon">{f.icon}</span>
                <strong>{f.label}</strong>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // SCREEN 2 — Paywall with 4 plans
  // ════════════════════════════════════════
  if (showPaywall) {
    return (
      <div className="setup-root">
        <AnimatedBg />
        <div className="paywall-root">

          <div className="paywall-header">
            <h2>Choose Your <span className="gradient-text">Plan</span></h2>
            <p>One-time payment. Valid for 30 days. No subscription, no hidden charges.</p>
            {error && <div className="error-msg">{error}</div>}
          </div>

          <div className="plans-grid">
            {PLANS.map(plan => (
              <div
                key={plan.id}
                className={`plan-card-new
                  ${plan.id === 'pro'     ? 'plan-popular' : ''}
                  ${plan.id === 'premium' ? 'plan-best'    : ''}`}
                style={{ '--plan-color': plan.color }}
              >
                {plan.badge && (
                  <div className="plan-top-badge"
                    style={{ background: plan.id === 'premium' ? '#f59e0b' : '#7c3aed' }}>
                    {plan.badge}
                  </div>
                )}

                <div className="plan-name-tag" style={{ background: plan.color }}>
                  {plan.name}
                </div>

                <div className="plan-price-block">
                  {plan.price ? (
                    <>
                      <span className="plan-price-big">
                        <span className="plan-rupee">₹</span>{plan.price}
                      </span>
                      <span className="plan-price-note">{plan.priceNote}</span>
                    </>
                  ) : (
                    <span className="plan-price-big plan-free-text">Free</span>
                  )}
                </div>

                <div className="plan-meta">
                  <span className="plan-interviews">{plan.interviews}</span>
                  <span className="plan-duration">🕐 {plan.duration}</span>
                </div>

                <div className="plan-divider" />

                <ul className="plan-features-new">
                  {plan.features.map((f, i) => (
                    <li key={i} className={f.active ? 'feat-active' : 'feat-inactive'}>
                      <span className="feat-icon">{f.active ? '✅' : '❌'}</span>
                      {f.text}
                    </li>
                  ))}
                </ul>

                <button
                  className={`plan-cta plan-cta-${plan.btnStyle}`}
                  disabled={loading}
                  onClick={() => {
                    if (plan.id === 'free') {
                      setShowPaywall(false);
                    } else {
                      handleSubscribe(plan.id);
                    }
                  }}
                >
                  {loading ? '...' : plan.btnText}
                </button>
              </div>
            ))}
          </div>

          <button
            className="back-link"
            onClick={() => { setShowPaywall(false); setUserId(null); onLogout(); }}
          >
            ← Use a different account
          </button>

        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // SCREEN 3 — Main interview setup form
  // ════════════════════════════════════════
  return (
    <div className="setup-root">
      <AnimatedBg />
      <div className="setup-container">

        <div className="user-topbar">
          <div className="user-info-pill">
            <span className="user-avatar-sm">
              {userStatus?.name?.charAt(0).toUpperCase() || '?'}
            </span>
            <div className="user-info-text">
              <span className="user-info-name">{userStatus?.name}</span>
              <span className="user-info-email">{userStatus?.email}</span>
            </div>
          </div>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={onViewHistory}>
              📋 My History
            </button>
            <button
              className="topbar-btn topbar-btn-outline"
              onClick={onLogout}
            >
              Sign Out
            </button>
          </div>
        </div>

        <div className="setup-header">
          <div className="aria-logo">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="23" stroke="url(#lg1b)" strokeWidth="2"/>
              <circle cx="24" cy="24" r="10" fill="url(#lg2b)" opacity="0.8"/>
              <circle cx="24" cy="24" r="5" fill="#fff" opacity="0.9"/>
              <defs>
                <linearGradient id="lg1b" x1="0" y1="0" x2="48" y2="48">
                  <stop stopColor="#6394ff"/><stop offset="1" stopColor="#a78bfa"/>
                </linearGradient>
                <linearGradient id="lg2b" x1="0" y1="0" x2="48" y2="48">
                  <stop stopColor="#6394ff"/><stop offset="1" stopColor="#f472b6"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h1 className="setup-title">Meet <span className="gradient-text">URU</span></h1>
          <p className="setup-subtitle">Your AI Technical Interviewer</p>
          <p className="setup-desc">
            URU will conduct a real verbal interview — asking questions, listening to your
            answers, and providing professional feedback. Just like a real interview.
          </p>
        </div>

        <div className="aria-preview">
          <div className="aria-avatar-small">
            <div className="avatar-ring" />
            <div className="avatar-ring ring-2" />
            <div className="avatar-core">
              <div className="avatar-face">
                <div className="avatar-eyes">
                  <span className="eye" /><span className="eye" />
                </div>
                <div className="avatar-smile" />
              </div>
            </div>
            <div className="pulse-dot" />
          </div>
          <div className="aria-intro-text">
            <strong>Hi {userStatus?.name}! I'm URU</strong>
            <p>Ready to interview you when you are ✨</p>
          </div>
        </div>

        {/* Plan status badge */}
        {userStatus && (
          <div className={`credit-status ${userStatus.isPaidPlan ? 'subscribed' : ''}`}>
            {userStatus.isPaidPlan && userStatus.isPlanActive
              ? `✅ ${userStatus.planDisplayName} plan active — ${userStatus.interviewsRemaining} interviews remaining`
              : `🎁 ${userStatus.interviewsRemaining} free interview${userStatus.interviewsRemaining !== 1 ? 's' : ''} remaining  •  ${userStatus.totalInterviewsTaken} taken so far`
            }
          </div>
        )}

        <div className="setup-form">
          <div className="form-row">
            <div className="form-group">
              <label>Job Role</label>
              <select value={form.jobRole}
                onChange={e => setForm({ ...form, jobRole: e.target.value })}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Tech Stack</label>
              <select value={form.techStack}
                onChange={e => setForm({ ...form, techStack: e.target.value })}>
                {STACKS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Experience Level</label>
            <div className="exp-pills">
              {EXPERIENCE_LEVELS.map(e => (
                <button key={e.value}
                  className={`exp-pill ${form.experienceLevel === e.value ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, experienceLevel: e.value })}>
                  <span className="exp-label">{e.label}</span>
                  <span className="exp-desc">{e.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="duration-badge">
            <span>🕐</span>
            <strong>30-Minute Interview</strong>
            <span>URU will ask questions for the full session duration</span>
          </div>

          {error && <div className="error-msg">{error}</div>}

          <button className="start-btn" onClick={handleStart} disabled={loading}>
            {loading
              ? <span className="btn-loading"><span className="spinner" /> Connecting to URU...</span>
              : <span>🎙️ Start Interview</span>
            }
          </button>
        </div>

        <div className="features-row">
          {[
            { icon: '🎤', label: 'Voice Recognition', desc: 'Speak your answers naturally'    },
            { icon: '🔊', label: 'AI Voice',          desc: 'URU speaks to you in real-time'  },
            { icon: '📊', label: 'Smart Evaluation',  desc: 'Detailed feedback & scoring'     },
          ].map(f => (
            <div className="feature-card" key={f.label}>
              <span className="feature-icon">{f.icon}</span>
              <strong>{f.label}</strong>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}